import { useState, useCallback } from 'react';
import { generateUniqueId } from '../../../../../utils/file_util';
import {
  WizardUploadFile,
  ProcessingPhase,
  PARSED_UPLOAD_TYPES,
  ParsedDocumentUploadResponse
} from '../../../../../types/upload';

/**
 * Simplified wizard step: 1 = Folder Drop, 2 = Processing/Upload
 */
export type SimplifiedWizardStep = 1 | 2;

/**
 * State for simplified 2-step wizard
 */
export interface SimplifiedWizardState {
  currentStep: SimplifiedWizardStep;
  // Files from dropped folder
  markdownFiles: WizardUploadFile[];
  assetFiles: WizardUploadFile[];
  sheetFiles: WizardUploadFile[];
  // Validation
  validationErrors: Array<{ code: number; message: string }>;
  validationWarnings: string[];
  // Processing
  processingPhase: ProcessingPhase;
  processingProgress: number;
  processingMessage: string;
  // Result
  uploadResult: ParsedDocumentUploadResponse | null;
}

/**
 * Result from processing dropped folder
 */
export interface FolderDropResult {
  valid: boolean;
  markdownCount: number;
  assetCount: number;
  sheetCount: number;
  errors: string[];
}

// File size limits
const MAX_MARKDOWN_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_IMAGE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_SHEET_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_FILENAME_LENGTH = 200;

const initialState: SimplifiedWizardState = {
  currentStep: 1,
  markdownFiles: [],
  assetFiles: [],
  sheetFiles: [],
  validationErrors: [],
  validationWarnings: [],
  processingPhase: 'idle',
  processingProgress: 0,
  processingMessage: '',
  uploadResult: null
};

function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? `.${parts.pop()?.toLowerCase()}` : '';
}

function getFileCategory(filename: string): 'markdown' | 'image' | 'sheet' | null {
  const ext = getFileExtension(filename);

  if (PARSED_UPLOAD_TYPES.markdown.includes(ext)) return 'markdown';
  if (PARSED_UPLOAD_TYPES.assets.includes(ext)) return 'image';
  if (PARSED_UPLOAD_TYPES.sheets.includes(ext)) return 'sheet';
  return null;
}

/**
 * Normalize filename for cross-platform comparison.
 * Vietnamese and other Unicode characters may have different representations (NFC vs NFD).
 */
function normalizeFilename(name: string): string {
  return name.normalize('NFC');
}

/**
 * Extract asset references from markdown content.
 * Parses patterns like:
 *   ![alt](assets/hash.jpg)     → "hash.jpg"
 *   ![alt](./assets/hash.jpg)   → "hash.jpg"
 *   `sheets/hash.csv`           → "hash.csv"
 */
function extractAssetReferencesFromContent(content: string): Set<string> {
  const refs = new Set<string>();

  // Match image refs: ![alt](assets/...) or ![alt](./assets/...)
  const imagePattern = /!\[[^\]]*\]\(\.?\/?assets\/([^)]+)\)/g;
  for (const match of content.matchAll(imagePattern)) {
    refs.add(normalizeFilename(match[1]).toLowerCase());
  }

  // Match sheet refs as markdown links: [text](sheets/...) or [text](./sheets/...)
  const sheetLinkPattern = /\[[^\]]*\]\(\.?\/?sheets\/([^)]+)\)/g;
  for (const match of content.matchAll(sheetLinkPattern)) {
    refs.add(normalizeFilename(match[1]).toLowerCase());
  }

  // Match sheet refs as backtick inline code: `sheets/hash.csv`
  const backtickPattern = /`sheets\/([^`]+)`/g;
  for (const match of content.matchAll(backtickPattern)) {
    refs.add(normalizeFilename(match[1]).toLowerCase());
  }

  return refs;
}

/**
 * Filter unreferenced assets based on markdown content analysis.
 * Reads markdown files to find referenced assets, removes unreferenced ones.
 */
async function filterUnreferencedAssetsByContent(
  markdownFiles: WizardUploadFile[],
  assetFiles: WizardUploadFile[],
  sheetFiles: WizardUploadFile[]
): Promise<{
  assetFiles: WizardUploadFile[];
  sheetFiles: WizardUploadFile[];
  removedCount: { assets: number; sheets: number };
}> {
  // Read all markdown files and collect asset references
  const allRefs = new Set<string>();
  for (const mdFile of markdownFiles) {
    try {
      const content = await mdFile.file.text();
      const refs = extractAssetReferencesFromContent(content);
      for (const ref of refs) {
        allRefs.add(ref);
      }
    } catch {
      // If we can't read a file, keep all assets (safe fallback)
      return {
        assetFiles,
        sheetFiles,
        removedCount: { assets: 0, sheets: 0 }
      };
    }
  }

  // If no references found at all, keep everything
  if (allRefs.size === 0) {
    return {
      assetFiles,
      sheetFiles,
      removedCount: { assets: 0, sheets: 0 }
    };
  }

  // Filter assets to only keep referenced ones
  const filteredAssets = assetFiles.filter((f) => allRefs.has(normalizeFilename(f.name).toLowerCase()));
  const filteredSheets = sheetFiles.filter((f) => allRefs.has(normalizeFilename(f.name).toLowerCase()));

  return {
    assetFiles: filteredAssets,
    sheetFiles: filteredSheets,
    removedCount: {
      assets: assetFiles.length - filteredAssets.length,
      sheets: sheetFiles.length - filteredSheets.length
    }
  };
}

export interface UseSimplifiedUploadWizardResult {
  state: SimplifiedWizardState;
  // Navigation
  goToStep: (step: SimplifiedWizardStep) => void;
  canProceed: () => boolean;
  // Folder handling
  handleFolderDrop: (files: FileList | File[]) => Promise<FolderDropResult>;
  // Processing
  setProcessingState: (phase: ProcessingPhase, progress?: number, message?: string) => void;
  setUploadResult: (result: ParsedDocumentUploadResponse) => void;
  // Reset
  reset: () => void;
  clearFiles: () => void;
}

export function useSimplifiedUploadWizard(): UseSimplifiedUploadWizardResult {
  const [state, setState] = useState<SimplifiedWizardState>(initialState);

  const goToStep = useCallback((step: SimplifiedWizardStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const canProceed = useCallback((): boolean => {
    switch (state.currentStep) {
      case 1:
        // Must have markdown files and no validation errors
        if (state.markdownFiles.length === 0) return false;
        if (state.validationErrors.length > 0) return false;
        return true;
      case 2:
        // Processing step - can proceed when complete
        return state.processingPhase === 'complete';
      default:
        return false;
    }
  }, [state]);

  /**
   * Process a dropped folder - categorize files and filter unreferenced assets
   */
  const handleFolderDrop = useCallback(async (files: FileList | File[]): Promise<FolderDropResult> => {
    const fileArray = Array.from(files);
    const errors: string[] = [];

    const markdownFiles: WizardUploadFile[] = [];
    const assetFiles: WizardUploadFile[] = [];
    const sheetFiles: WizardUploadFile[] = [];

    // Categorize files
    for (const file of fileArray) {
      const category = getFileCategory(file.name);
      if (!category) continue;

      // Validate file size
      const maxSize =
        category === 'markdown' ? MAX_MARKDOWN_SIZE : category === 'image' ? MAX_IMAGE_SIZE : MAX_SHEET_SIZE;

      if (file.size > maxSize) {
        const sizeLabel = category === 'markdown' ? '10MB' : category === 'image' ? '50MB' : '100MB';
        errors.push(`${file.name}: Tệp quá lớn (tối đa ${sizeLabel})`);
        continue;
      }

      if (file.name.length > MAX_FILENAME_LENGTH) {
        errors.push(`${file.name.substring(0, 30)}...: Tên tệp quá dài`);
        continue;
      }

      const wizardFile: WizardUploadFile = {
        uid: generateUniqueId(),
        name: file.name,
        file,
        size: file.size,
        type: category as 'markdown' | 'image' | 'sheet',
        status: 'pending',
        relativePath: file.webkitRelativePath || undefined
      };

      switch (category) {
        case 'markdown':
          markdownFiles.push(wizardFile);
          break;
        case 'image':
          assetFiles.push(wizardFile);
          break;
        case 'sheet':
          sheetFiles.push(wizardFile);
          break;
      }
    }

    // Filter unreferenced assets by parsing markdown content
    let filteredAssets = assetFiles;
    let filteredSheets = sheetFiles;
    const validationWarnings: string[] = [];

    if (markdownFiles.length > 0 && (assetFiles.length > 0 || sheetFiles.length > 0)) {
      const {
        assetFiles: filtered,
        sheetFiles: filteredS,
        removedCount
      } = await filterUnreferencedAssetsByContent(markdownFiles, assetFiles, sheetFiles);
      filteredAssets = filtered;
      filteredSheets = filteredS;

      if (removedCount.assets > 0 || removedCount.sheets > 0) {
        console.log(
          `[Folder Drop] Filtered ${removedCount.assets} unreferenced assets, ` +
            `${removedCount.sheets} unreferenced sheets`
        );
        if (removedCount.assets > 0) {
          validationWarnings.push(
            `Đã loại bỏ ${removedCount.assets} hình ảnh không được tham chiếu trong markdown`
          );
        }
        if (removedCount.sheets > 0) {
          validationWarnings.push(
            `Đã loại bỏ ${removedCount.sheets} bảng tính không được tham chiếu trong markdown`
          );
        }
      }
    }

    // Update state
    setState((prev) => ({
      ...prev,
      markdownFiles,
      assetFiles: filteredAssets,
      sheetFiles: filteredSheets,
      validationErrors: errors.length > 0 ? errors.map((e) => ({ code: 10000, message: e })) : [],
      validationWarnings
    }));

    const valid = markdownFiles.length > 0 && errors.length === 0;

    return {
      valid,
      markdownCount: markdownFiles.length,
      assetCount: filteredAssets.length,
      sheetCount: filteredSheets.length,
      errors
    };
  }, []);

  const setProcessingState = useCallback((phase: ProcessingPhase, progress?: number, message?: string) => {
    setState((prev) => ({
      ...prev,
      processingPhase: phase,
      processingProgress: progress ?? prev.processingProgress,
      processingMessage: message ?? prev.processingMessage
    }));
  }, []);

  const setUploadResult = useCallback((result: ParsedDocumentUploadResponse) => {
    setState((prev) => ({
      ...prev,
      uploadResult: result
    }));
  }, []);

  const clearFiles = useCallback(() => {
    setState((prev) => ({
      ...prev,
      markdownFiles: [],
      assetFiles: [],
      sheetFiles: [],
      validationErrors: [],
      validationWarnings: []
    }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    state,
    goToStep,
    canProceed,
    handleFolderDrop,
    setProcessingState,
    setUploadResult,
    reset,
    clearFiles
  };
}
